import * as React from "react";
import { cn } from "./cn";
import { Card } from "./card";

export interface PillarCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Icon node (e.g. a lucide-react icon element). Decorative. */
  icon?: React.ReactNode;
  /** Pillar heading (e.g. "Writing Mastery"). */
  title: React.ReactNode;
  /** Supporting copy. Provide via `description` or children. */
  description?: React.ReactNode;
  /** Heading level for the title. @default "h3" */
  as?: "h2" | "h3" | "h4";
}

/**
 * Feature / value "pillar" tile used in 3-up grids (home page pillars, program
 * highlights). Icon sits in a soft accent chip above the title.
 */
export const PillarCard = React.forwardRef<HTMLDivElement, PillarCardProps>(
  ({ className, icon, title, description, as: Heading = "h3", children, ...props }, ref) => {
    return (
      <Card ref={ref} className={cn("flex h-full flex-col gap-4 p-6", className)} {...props}>
        {icon && (
          <span
            aria-hidden="true"
            className="inline-flex h-12 w-12 items-center justify-center rounded-card bg-accent/15 text-primary"
          >
            {icon}
          </span>
        )}
        <Heading className="font-heading text-xl font-bold text-ink">{title}</Heading>
        {(description ?? children) && (
          <div className="text-sm leading-relaxed text-muted">{description ?? children}</div>
        )}
      </Card>
    );
  },
);
PillarCard.displayName = "PillarCard";
